<template>
  <SparkModal :model-value="show" class="h-full" @update:model-value="close">
    <template #default>
      <div v-if="currentView === 'supplier'">
        <div
          class="text-18 font-bold mb-16"
          v-text="
            currentView === 'supplier'
              ? supplierMode === 'create'
                ? 'Create Supplier'
                : 'Edit Supplier'
              : 'Production Option'
          "
        />

        <VeeValidateForm @submit="handleSubmit">
          <SparkInput v-model="supplierName" class="mb-32" name="supplierName" label="Name *" :rules="validateName" />
          <SparkInput
            v-model="supplierEmail"
            class="mb-32"
            name="supplierEmail"
            label="Email *"
            :rules="validateEmail"
          />

          <div class="mb-32 flex flex-col justify-start items-start">
            <label class="text-13 text-gray-500 block mb-8">Logo</label>
            <div class="flex items-center gap-16">
              <SparkButton custom="!w-120" variant="outlined" @click.prevent="triggerFileInput">
                <div v-text="'Upload'" />
              </SparkButton>
              <input
                ref="fileInput"
                type="file"
                class="hidden"
                accept=".jpeg, .jpg, .png, .svg"
                @change="handleFileUpload"
              />
              <div v-if="previewImage" class="relative flex items-center group">
                <img :src="previewImage" alt="Logo preview" class="max-h-40 max-w-100" />
                <div class="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <SparkButtonIcon
                    icon="fas fa-times"
                    variant="text"
                    size="sm"
                    custom="bg-gray-50 rounded-full hover:bg-red-100 hover:text-red-500"
                    @click="deleteImage"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-between border border-gray-100 rounded-8 p-8 items-center mb-32">
            <label class="text-13 text-gray-500">Qualified</label>
            <SparkSwitch v-model="isQualified" name="isQualified" />
          </div>

          <div class="mb-32">
            <div class="text-13 text-gray-500 mb-8">Production Options</div>
            <div
              v-if="!temporaryCombinations?.length"
              class="border border-gray-100 rounded-8 p-24 flex flex-col items-center justify-center text-center"
            >
              <div class="text-14 font-medium mb-4">No production options added yet.</div>
              <div class="text-13 text-gray-500 mb-16">Get started and add a first production option</div>
              <SparkButton variant="outlined" @click="showCombinationView">
                <div class="flex items-center">
                  <i class="fas fa-plus mr-8" />
                  <span>Add production option</span>
                </div>
              </SparkButton>
            </div>
            <div v-else class="border border-gray-100 rounded-8">
              <div
                v-for="(combination, index) in temporaryCombinations"
                :key="index"
                class="p-16 flex items-center justify-between border-b border-gray-100 last:border-b-0"
              >
                <div class="flex items-center gap-8">
                  <i v-if="combination.calculation_method === 'ai'" class="flex items-center gap-4">
                    <img src="@/assets/icons/supplier/stars-02.svg" />
                  </i>
                  <i v-else class="flex items-center gap-4">
                    <img src="@/assets/icons/supplier/line-chart-up-05.svg" />
                  </i>
                  <div>
                    <div class="text-14 font-medium">{{ combination.name || `Combination ${index + 1}` }}</div>
                    <div class="text-13 text-gray-500">
                      Process: {{ getPrcName(combination.process_uid) }}
                      <template v-if="combination.material_id">
                        , Material: {{ getMaterialName(combination.material_id) }}
                      </template>
                      <template v-if="combination.calculation_method === 'reverse'">
                        , Pricing configuration:
                        {{ combination.pricing_config_name || getPricingConfigName(combination.pricing_config_uid) }}
                      </template>
                    </div>
                  </div>
                </div>
                <SparkButtonIcon
                  icon="fas fa-trash"
                  variant="text"
                  size="sm"
                  custom="text-gray-500 hover:text-red-500"
                  @click="removeCombination(combination)"
                />
              </div>
              <div class="p-16 border-t border-gray-100">
                <SparkButton variant="outlined" type="button" @click="showCombinationView">
                  <div class="flex items-center">
                    <i class="fas fa-plus mr-8" />
                    <span>Add production option</span>
                  </div>
                </SparkButton>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-16 mt-[100px]">
            <SparkButton variant="outlined" @click="close">
              <div v-text="'Cancel'" />
            </SparkButton>
            <SparkButton variant="secondary" type="submit">
              <div v-text="supplierMode === 'create' ? 'Create Email Supplier' : 'Save Changes'" />
            </SparkButton>
          </div>
        </VeeValidateForm>
      </div>

      <div v-else>
        <div class="mb-16">
          <SparkButton variant="outlined" @click="goBack">
            <div class="flex items-center">
              <i class="fas fa-arrow-left mr-2" />
              <span>Back</span>
            </div>
          </SparkButton>
        </div>

        <div
          class="text-18 font-bold mb-16"
          v-text="currentView === 'supplier' ? 'Create Supplier' : 'Production Option'"
        />

        <div class="mb-24">
          <SparkSelect
            v-model="selectedProcess"
            name="selectedProcess"
            label="Process *"
            placeholder="Select"
            :options="prcAcronymOptions"
            class="mb-16"
          />
        </div>

        <div class="mb-24">
          <SparkSelect
            v-model="selectedMaterial"
            name="selectedMaterial"
            label="Material"
            placeholder="Select"
            :options="materialOptions"
            class="mb-16"
            :disabled="isMaterialsLoading"
          />
          <div v-if="isMaterialsLoading" class="text-13 text-gray-500 mb-8">Loading materials...</div>
          <div
            v-else-if="materialOptions.filter(m => m.value !== null).length === 0"
            class="text-13 text-gray-500 mb-8"
          >
            No materials were found. You can still create a production option using only a process, but this will be
            less accurate.
          </div>
        </div>

        <div class="mb-24">
          <div class="text-16 font-semibold mb-8">Calculation method</div>
          <div class="flex gap-16">
            <div
              class="border rounded-8 p-16 flex-1 cursor-pointer"
              :class="calculationMethod === 'ai' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'"
              @click="calculationMethod = 'ai'"
            >
              <div class="flex items-center mb-8">
                <img src="@/assets/icons/supplier/stars-02.svg" class="mr-8" />
                <span class="font-semibold">AI estimated</span>
              </div>
              <p class="text-13 text-gray-600">
                This algorithm is trained using 3D Spark pricing data to ensure accurate and reliable results.
              </p>
            </div>
            <div
              class="border rounded-8 p-16 flex-1 cursor-pointer"
              :class="calculationMethod === 'reverse' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'"
              @click="calculationMethod = 'reverse'"
            >
              <div class="flex items-center mb-8">
                <img src="@/assets/icons/supplier/line-chart-up-05.svg" class="mr-8" />
                <span class="font-semibold">Reverse Engineered</span>
              </div>
              <p class="text-13 text-gray-600">
                Pricing is determined based on existing quotes from the supplier. The more quotes available, the greater
                the accuracy of the pricing. To enable this, the following inputs are required: at least 10 supplier
                quotes, including part ID, size, and CAD files.
              </p>
            </div>
          </div>
        </div>

        <div v-if="calculationMethod === 'reverse'" class="mb-24">
          <SparkSelect
            v-model="selectedPricingConfig"
            name="selectedPricingConfig"
            label="Pricing Configuration"
            placeholder="Select pricing configuration"
            :options="pricingConfigOptions"
            class="mb-16"
          />
        </div>

        <div class="flex justify-end gap-16 mt-[100px]">
          <SparkButton variant="outlined" @click="close">
            <div v-text="'Cancel'" />
          </SparkButton>
          <SparkButton variant="secondary" @click="addCombination">
            <div v-text="combinationMode === 'edit' ? 'Save changes' : 'Add production option'" />
          </SparkButton>
        </div>
      </div>
    </template>
  </SparkModal>
</template>

<script>
import { Form as VeeValidateForm } from 'vee-validate';
import VueMultiselect from 'vue-multiselect';
import { mapActions, mapMutations, mapState } from 'vuex';
import { z } from 'zod';

import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkButtonIcon from '@/components/SparkComponents/SparkButtonIcon.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import SparkModal from '@/components/SparkComponents/SparkModal.vue';
import SparkSelect from '@/components/SparkComponents/SparkSelect.vue';
import SparkSwitch from '@/components/SparkComponents/SparkSwitch.vue';

export default {
  name: 'CreateSupplierModal',

  components: {
    SparkButton,
    SparkModal,
    SparkInput,
    VueMultiselect,
    VeeValidateForm,
    SparkSwitch,
    SparkButtonIcon,
    SparkSelect,
  },

  props: {
    show: { type: Boolean },
    supplier: { type: Object, default: null },
    supplierMode: { type: String, default: 'create' },
  },

  emits: ['close'],

  data() {
    return {
      currentView: 'supplier',
      supplierName: '',
      supplierEmail: '',
      logoFile: null,
      previewImage: null,
      selectedOptions: [],
      isQualified: false,

      // Combination view data
      selectedProcess: null,
      selectedMaterial: null,
      calculationMethod: 'ai',
      selectedPricingConfig: null,
      prcAcronymOptions: [],
      materialOptions: [],
      combinationMode: 'create',
      isMaterialsLoading: false,
      dataLoadedFromStorage: false,

      supplierCreatedDuringPricing: false,
      createdSupplierUid: null,
      isCompletingCreation: false,
    };
  },

  computed: {
    ...mapState(['processes', 'organization']),
    ...mapState('application', ['axiosInstance']),
    ...mapState('supplier', ['temporaryCombinations', 'pricingConfigs']),

    processOptions() {
      return Object.keys(this.processes).map(key => ({
        value: this.processes[key].value,
        label: this.processes[key].text,
      }));
    },

    getPrcName() {
      return processUid => {
        const process = this.prcAcronymOptions.find(p => p.value === processUid);
        return process ? process.label : processUid;
      };
    },

    getMaterialName() {
      return materialId => {
        const material = this.materialOptions.find(m => m.value === materialId);
        return material ? material.label : materialId;
      };
    },

    getPricingConfigName() {
      return configUid => {
        const config = (this.pricingConfigs || []).find(c => c.value === configUid);
        return config ? config.label : configUid;
      };
    },

    pricingConfigOptions() {
      if (this.pricingConfigs && this.pricingConfigs.length > 0) {
        if (this.pricingConfigs[0].hasOwnProperty('value') && this.pricingConfigs[0].hasOwnProperty('label')) {
          return this.pricingConfigs;
        }

        return (this.pricingConfigs || []).map(config => ({
          value: config.uid,
          label: config.name || config.uid,
        }));
      }

      return [];
    },
  },

  watch: {
    prcAcronymOptions: {
      handler(newOptions) {
        if (newOptions.length > 0 && this.selectedPrcAcronym) {
          const selected = newOptions.find(option => option.value === this.selectedPrcAcronym);
          if (selected) {
            const currentValue = this.selectedPrcAcronym;
            this.selectedPrcAcronym = null;
            this.$nextTick(() => {
              this.selectedPrcAcronym = currentValue;
            });
          }
        }
      },

      immediate: true,
    },

    selectedProcess(newValue) {
      if (newValue) {
        this.fetchProcessMaterials(newValue);
      } else {
        this.fetchMaterials();
      }
    },

    supplier: {
      handler(newSupplier) {
        if (newSupplier && this.supplierMode === 'edit') {
          this.supplierName = newSupplier.name;
          this.supplierEmail = newSupplier.contact_email;
          this.isQualified = Boolean(newSupplier.is_qualified);
          this.selectedOptions = newSupplier.selectedOptions || [];
          if (newSupplier.previewImage?.startsWith('data:')) {
            this.previewImage = newSupplier.previewImage;
          }

          const combinations = newSupplier.supplier_combinations || [];
          this.setTemporaryCombination(combinations);
        } else if (this.supplierMode === 'create') {
          this.resetForm();
        }
      },

      immediate: true,
    },

    show: {
      handler(newValue) {
        if (newValue) {
          if (this.supplierMode === 'create') {
            // If parent component has data, use that first
            if (this.supplier) {
              this.supplierName = this.supplier.name || '';
              this.supplierEmail = this.supplier.email || '';
              this.isQualified = !!this.supplier.isQualified;
              this.previewImage = this.supplier.previewImage || null;

              if (this.supplier.temporaryCombinations && this.supplier.temporaryCombinations.length > 0) {
                this.setTemporaryCombination(this.supplier.temporaryCombinations);
              }

              if (this.supplier.currentCombination) {
                this.selectedProcess = this.supplier.currentCombination.selectedProcess;
                this.selectedMaterial = this.supplier.currentCombination.selectedMaterial;
                this.calculationMethod = this.supplier.currentCombination.calculationMethod || 'ai';

                if (this.supplier.currentCombination.newPricingConfig?.configId) {
                  this.selectedPricingConfig = this.supplier.currentCombination.newPricingConfig.configId;

                  this.$nextTick(() => {
                    this.currentView = 'combination';
                    this.fetchPricingConfigs().then(async () => {
                      try {
                        const configName = await this.fetchPricingConfigName(this.selectedPricingConfig);

                        if (configName && configName !== this.selectedPricingConfig) {
                          this.supplier.currentCombination.newPricingConfig.configName = configName;
                        }
                      } catch (error) {
                        console.error('Error fetching pricing config name:', error);
                      }
                    });
                  });
                } else if (this.supplier.currentCombination.currentView === 'combination') {
                  // If no new config but we were in combination view, go back there
                  this.currentView = 'combination';
                }
              }
            }
            // If no data from parent, check if there's data in localStorage that hasn't been loaded yet
            else if (!this.dataLoadedFromStorage) {
              this.loadDataFromStorage();
            } else {
              this.resetForm();
              this.clearTemporaryCombinations();
            }
          } else if (this.supplierMode === 'edit') {
            // Handle edit mode - load data from localStorage if returning from pricing
            const supplierDataString = localStorage.getItem('pendingSupplierData');
            if (supplierDataString && !this.dataLoadedFromStorage) {
              this.loadDataFromStorage();
            }
          }
        }
      },
      immediate: true,
    },

    supplierMode: {
      handler(newMode) {
        if (newMode === 'create' && this.show) {
          this.resetForm();
          this.clearTemporaryCombinations();
        }
      },
      immediate: true,
    },

    calculationMethod: {
      handler: 'watchCalculationMethod',
      immediate: true,
    },

    selectedPricingConfig: {
      handler: 'watchSelectedPricingConfig',
      immediate: false,
    },
  },

  mounted() {
    this.fetchMaterials();
    this.fetchPrcAcronyms();
    this.fetchOrganization();

    if (!this.show) {
      this.loadDataFromStorage();
    }
  },

  methods: {
    ...mapActions(['fetchOrganization']),
    ...mapActions('supplier', [
      'createEmailSupplier',
      'updateSuppliers',
      'updateSupplierAction',
      'fetchPricingConfigs',
      'deleteSupplier',
    ]),
    ...mapMutations('supplier', ['addTemporaryCombination', 'clearTemporaryCombinations', 'setTemporaryCombination']),

    async fetchMaterials() {
      this.isMaterialsLoading = true;
      await this.axiosInstance
        .get(`api/v1/default-materials/`)
        .then(response => {
          this.materialOptions = response.data.map(entry => ({
            value: entry.mat_id,
            label: entry.mat_name,
          }));
          this.materialOptions.unshift({ label: ' ', value: null });
        })
        .catch(error => {
          console.error('Error fetching materials:', error);
        })
        .finally(() => {
          this.isMaterialsLoading = false;
        });
    },

    async fetchPrcAcronyms() {
      await this.axiosInstance
        .get(`api/v1/supplier-prc-acronyms/`)
        .then(response => {
          this.prcAcronymOptions = response.data.map(entry => ({
            value: entry.uid,
            label: entry.prc_name,
          }));
          this.prcAcronymOptions.unshift({ label: ' ', value: null });
        })
        .catch(error => {
          console.error('Error fetching PRC acronyms:', error);
        });
    },

    async fetchPricingConfigName(configUid) {
      try {
        const response = await this.axiosInstance.get(`api/v1/pricing-config/${configUid}/`);
        return response.data.name;
      } catch (error) {
        // Don't log 405 errors bc it takes some time to load pricing configs names
        if (error.response?.status !== 405) {
          console.error('Error fetching pricing config name:', error);
        }
        return null;
      }
    },

    close() {
      this.currentView = 'supplier';

      if (this.supplierMode === 'create') {
        if (this.supplierCreatedDuringPricing && this.createdSupplierUid && !this.isCompletingCreation) {
          this.deleteSupplierIfNeeded();
        }
        this.resetForm();
      }

      localStorage.removeItem('pendingSupplierData');
      localStorage.removeItem('pricingOrigin');

      this.$emit('close');
    },

    validateName(value) {
      try {
        z.string().min(1, 'Name is required').parse(value);
        return true;
      } catch (error) {
        return error.errors[0].message;
      }
    },

    handleSubmit() {
      if (this.supplierMode === 'edit') {
        this.updateSupplier();
      } else {
        this.createSupplier();
      }
    },

    validateEmail(value) {
      try {
        z.string().min(1, 'Email is required').email('Invalid email format').parse(value);
        return true;
      } catch (error) {
        return error.errors[0].message;
      }
    },

    async createSupplier() {
      try {
        if (this.supplierCreatedDuringPricing && this.createdSupplierUid) {
          let data = {
            combinations: JSON.stringify(this.temporaryCombinations),
          };

          if (this.selectedOptions.length > 0) {
            const selected = this.selectedOptions.map(option => option.value);
            data.supplier_processes = selected;
          }

          await this.updateSupplierAction({
            data,
            uid: this.createdSupplierUid,
            notify: this.$root.notify,
          });

          this.$root.notify('success', 'Supplier updated successfully', 3000);
          this.isCompletingCreation = true; // Mark as successfully completing
          localStorage.removeItem('pendingSupplierData');
          localStorage.removeItem('pricingOrigin');
          this.close();
          return;
        }

        let data = {
          name: this.supplierName,
          email: this.supplierEmail,
          is_qualified: this.isQualified,
          combinations: JSON.stringify(this.temporaryCombinations),
          file: this.logoFile,
          organization_uid: this.organization.uid,
        };

        if (this.selectedOptions.length > 0) {
          const selected = this.selectedOptions.map(option => option.value);
          data.supplier_processes = selected;
        }

        await this.createEmailSupplier({ data, notify: this.$root.notify });

        localStorage.removeItem('pendingSupplierData');
        localStorage.removeItem('pricingOrigin');

        this.close();
      } catch (error) {
        console.error('Error creating supplier:', error);
      }
    },

    async updateSupplier() {
      let supplierUid = this.supplier?.uid;

      if (!supplierUid) {
        if (this.createdSupplierUid) {
          supplierUid = this.createdSupplierUid;
        } else {
          const supplierDataString = localStorage.getItem('pendingSupplierData');
          if (supplierDataString) {
            try {
              const supplierData = JSON.parse(supplierDataString);
              if (supplierData.existingSupplierUid) {
                supplierUid = supplierData.existingSupplierUid;
              } else if (supplierData.createdSupplierUid) {
                supplierUid = supplierData.createdSupplierUid;
              }
            } catch (e) {
              console.error('Error parsing supplier data from localStorage:', e);
            }
          }
        }
      }

      if (!supplierUid) {
        this.$root.notify('error', 'Could not identify supplier. Please try again.', 3000);
        return;
      }

      let data = {};
      data.file = this.logoFile;
      data.organization_uid = this.organization.uid;

      if (this.supplierName !== this.supplier.name) {
        data.name = this.supplierName;
      }

      if (this.supplierEmail !== this.supplier.email) {
        data.email = this.supplierEmail;
      }

      if (this.isQualified !== this.supplier.is_qualified) {
        data.is_qualified = this.isQualified;
      }

      const currentCombinations = JSON.stringify(this.temporaryCombinations);
      const originalCombinations = JSON.stringify(this.supplier.supplier_combinations);
      if (currentCombinations !== originalCombinations) {
        data.combinations = currentCombinations;
      }

      try {
        await this.updateSupplierAction({
          data,
          uid: supplierUid,
          notify: this.$root.notify,
        });

        const supplierDataString = localStorage.getItem('pendingSupplierData');
        if (supplierDataString) {
          try {
            const supplierData = JSON.parse(supplierDataString);
            if (supplierData.currentCombination?.newPricingConfig?.configId) {
              this.$root.notify('success', 'Supplier updated with new pricing configuration', 3000);
            } else {
              this.$root.notify('success', 'Supplier updated successfully', 3000);
            }
          } catch (e) {
            this.$root.notify('success', 'Supplier updated successfully', 3000);
          }
        } else {
          this.$root.notify('success', 'Supplier updated successfully', 3000);
        }

        this.close();
      } catch (error) {
        console.error('Error in component:', error);
      }
    },

    resetForm() {
      this.supplierName = '';
      this.supplierEmail = '';
      this.logoFile = null;
      this.previewImage = null;
      this.selectedOptions = [];
      this.isQualified = false;
      this.currentView = 'supplier';
      this.clearTemporaryCombinations();
      this.dataLoadedFromStorage = false;
      this.isCompletingCreation = false;
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.logoFile = file;
        const reader = new FileReader();
        reader.onload = e => {
          this.previewImage = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    deleteImage() {
      this.logoFile = null;
      this.previewImage = null;
      this.$refs.fileInput.value = '';
    },

    showCombinationView() {
      this.currentView = 'combination';
      if (this.calculationMethod === 'reverse') {
        this.fetchPricingConfigs().catch(error => {
          this.$root.notify('error', 'Failed to fetch pricing configurations', 3000);
        });
      }
    },

    goBack() {
      this.currentView = 'supplier';
    },

    async addCombination() {
      if (!this.selectedProcess) {
        this.$root.notify('error', 'Please select a process', 3000);
        return;
      }

      // Check for existing combination
      const existingCombination = this.temporaryCombinations.find(
        combination =>
          combination.process_uid === this.selectedProcess &&
          combination.material_id === this.selectedMaterial &&
          combination.calculation_method === this.calculationMethod
      );

      if (existingCombination) {
        this.$root.notify(
          'warning',
          `This combination already exists: ${this.getPrcName(this.selectedProcess)}${
            this.selectedMaterial ? ` with ${this.getMaterialName(this.selectedMaterial)}` : ''
          } (${this.calculationMethod.toUpperCase()})`,
          3000
        );
        return;
      }

      const combination = {
        name: `Combination ${this.temporaryCombinations.length + 1}`,
        process_uid: this.selectedProcess,
        material_id: this.selectedMaterial,
        calculation_method: this.calculationMethod,
      };

      if (this.calculationMethod === 'reverse') {
        if (!this.selectedPricingConfig) {
          this.$root.notify('error', 'Please select a pricing configuration', 3000);
          return;
        }
        combination.pricing_config_uid = this.selectedPricingConfig;
      }

      this.addTemporaryCombination(combination);

      if (this.supplierCreatedDuringPricing && this.createdSupplierUid) {
        try {
          await this.updateSupplierAction({
            data: {
              combinations: JSON.stringify(this.temporaryCombinations),
            },
            uid: this.createdSupplierUid,
            notify: this.$root.notify,
          });
        } catch (error) {
          console.error('Error updating supplier combinations:', error);
          this.$root.notify('error', 'Failed to update supplier combinations', 3000);
        }
      }

      // Reset form fields
      this.selectedProcess = null;
      this.selectedMaterial = null;
      this.calculationMethod = 'ai';
      this.selectedPricingConfig = null;

      this.currentView = 'supplier';
    },

    async removeCombination(combination) {
      const tempCombination = this.temporaryCombinations.filter(c => c.uid !== combination.uid);
      this.setTemporaryCombination(tempCombination);

      let supplierUid = this.supplier?.uid;

      if (!supplierUid) {
        if (this.createdSupplierUid) {
          supplierUid = this.createdSupplierUid;
        } else {
          const supplierDataString = localStorage.getItem('pendingSupplierData');
          if (supplierDataString) {
            try {
              const supplierData = JSON.parse(supplierDataString);
              if (supplierData.existingSupplierUid) {
                supplierUid = supplierData.existingSupplierUid;
              } else if (supplierData.createdSupplierUid) {
                supplierUid = supplierData.createdSupplierUid;
              }
            } catch (e) {
              console.error('Error parsing supplier data from localStorage:', e);
            }
          }
        }
      }

      // Save the changes to the backend
      if (supplierUid) {
        try {
          await this.updateSupplierAction({
            data: {
              combinations: JSON.stringify(tempCombination),
            },
            uid: supplierUid,
            notify: this.$root.notify,
          });
        } catch (error) {
          console.error('Error updating supplier combinations:', error);
          this.$root.notify('error', 'Failed to remove combination', 3000);
        }
      }
    },

    async fetchProcessMaterials(processUid) {
      try {
        const response = await this.axiosInstance.get('api/v1/process-materials/', {
          params: {
            process_uid: processUid,
            calculation_method: this.calculationMethod,
          },
        });

        this.materialOptions = response.data.map(entry => ({
          value: entry.mat_id,
          label: entry.mat_name,
          hasAiPricing: Boolean(entry.has_ai_pricing),
        }));
      } catch (error) {
        console.error(error);
        this.fetchMaterials();
      }
    },

    watchCalculationMethod(newMethod) {
      if (newMethod === 'reverse') {
        this.fetchPricingConfigs().catch(error => {
          this.$root.notify('error', 'Failed to fetch pricing configurations', 3000);
        });
      }
      if (this.selectedProcess) {
        this.fetchProcessMaterials(this.selectedProcess);
      }
    },

    watchSelectedPricingConfig(newValue) {
      if (newValue === 'new') {
        this.selectedPricingConfig = null;

        if (this.supplierMode === 'create') {
          this.createSupplierForPricingConfig();
        } else if (this.supplierMode === 'edit') {
          this.navigateToPricingConfig();
        }
      }
    },

    async createSupplierForPricingConfig() {
      try {
        // Validate required fields
        if (!this.supplierName || !this.supplierEmail) {
          this.$root.notify(
            'error',
            'Please fill in supplier name and email before creating pricing configuration',
            3000
          );
          return;
        }

        let data = {
          name: this.supplierName,
          email: this.supplierEmail,
          is_qualified: this.isQualified,
          combinations: JSON.stringify(this.temporaryCombinations),
          file: this.logoFile,
          organization_uid: this.organization.uid,
        };

        if (this.selectedOptions.length > 0) {
          const selected = this.selectedOptions.map(option => option.value);
          data.supplier_processes = selected;
        }

        // Create supplier without combinations (will be added later)
        const response = await this.axiosInstance.post('/api/v1/supplier-management/', data);
        const newSupplier = response.data;

        this.createdSupplierUid = newSupplier.uid;
        this.supplierCreatedDuringPricing = true;

        // Store supplier data for return
        const supplierData = {
          name: this.supplierName,
          email: this.supplierEmail,
          isQualified: this.isQualified,
          previewImage: this.previewImage,
          logoFile: this.logoFile ? true : false,
          selectedOptions: this.selectedOptions,
          temporaryCombinations: this.temporaryCombinations,
          currentCombination: {
            selectedProcess: this.selectedProcess,
            selectedMaterial: this.selectedMaterial,
            calculationMethod: this.calculationMethod,
            currentView: 'combination',
          },
          createdSupplierUid: newSupplier.uid,
          organization_uid: this.organization.uid,
        };

        localStorage.setItem('pendingSupplierData', JSON.stringify(supplierData));
        localStorage.setItem('pricingOrigin', 'supplier');

        this.$root.notify(
          'success',
          'Supplier created successfully. You can now create the pricing configuration.',
          3000
        );

        this.$router.push({
          name: 'pricing',
          query: { source: 'supplier' },
        });
      } catch (error) {
        console.error('Error creating supplier for pricing config:', error);
        this.$root.notify('error', 'Failed to create supplier. Please try again.', 3000);
      }
    },

    async navigateToPricingConfig() {
      try {
        const supplierData = {
          name: this.supplierName,
          email: this.supplierEmail,
          isQualified: this.isQualified,
          previewImage: this.previewImage,
          logoFile: this.logoFile ? true : false,
          selectedOptions: this.selectedOptions,
          temporaryCombinations: this.temporaryCombinations,
          currentCombination: {
            selectedProcess: this.selectedProcess,
            selectedMaterial: this.selectedMaterial,
            calculationMethod: this.calculationMethod,
            currentView: 'combination',
          },
          existingSupplierUid: this.supplier.uid,
          supplierMode: 'edit',
          organization_uid: this.organization.uid,
        };

        localStorage.setItem('pendingSupplierData', JSON.stringify(supplierData));
        localStorage.setItem('pricingOrigin', 'supplier');

        this.$root.notify('success', 'You can now create the pricing configuration.', 3000);

        this.$router.push({
          name: 'pricing',
          query: { source: 'supplier' },
        });
      } catch (error) {
        console.error('Error navigating to pricing config:', error);
        this.$root.notify('error', 'Failed to navigate to pricing configuration. Please try again.', 3000);
      }
    },

    async deleteSupplierIfNeeded() {
      if (this.supplierCreatedDuringPricing && this.createdSupplierUid) {
        try {
          await this.deleteSupplier({ supplierUid: this.createdSupplierUid, notify: this.$root.notify });
          // Supplier deleted due to cancellation
        } catch (error) {
          console.error('Error deleting supplier:', error);
        }
      }
    },

    async loadDataFromStorage() {
      const supplierDataString = localStorage.getItem('pendingSupplierData');
      if (supplierDataString) {
        try {
          const supplierData = JSON.parse(supplierDataString);

          this.dataLoadedFromStorage = true;

          if (this.supplierMode === 'edit' && supplierData.existingSupplierUid) {
            if (this.supplier) {
              this.supplier.uid = supplierData.existingSupplierUid;
            }
          }

          if (this.supplierMode === 'create' || !this.supplier) {
            this.supplierName = supplierData.name || '';
            this.supplierEmail = supplierData.email || supplierData.contact_email || '';
            this.isQualified = !!supplierData.isQualified;
            this.previewImage = supplierData.previewImage || null;
            this.selectedOptions = supplierData.selectedOptions || [];
          }

          if (this.supplierMode === 'create') {
            if (supplierData.temporaryCombinations && supplierData.temporaryCombinations.length > 0) {
              this.setTemporaryCombination(supplierData.temporaryCombinations);
            } else {
              this.clearTemporaryCombinations();
            }
          } else {
            // For edit mode, keep the existing temporary combinations from the supplier
            // Don't clear or modify temporary combinations in edit mode
            // They should be loaded by the supplier watcher
          }

          if (supplierData.currentCombination) {
            this.selectedProcess = supplierData.currentCombination.selectedProcess;
            this.selectedMaterial = supplierData.currentCombination.selectedMaterial;
            this.calculationMethod = supplierData.currentCombination.calculationMethod || 'ai';

            if (supplierData.currentCombination.currentView === 'combination') {
              this.currentView = 'combination';
            }

            if (supplierData.currentCombination.newPricingConfig?.configId) {
              this.selectedPricingConfig = supplierData.currentCombination.newPricingConfig.configId;

              this.$nextTick(() => {
                this.currentView = 'combination';
                this.fetchPricingConfigs().then(async () => {
                  try {
                    const configName = await this.fetchPricingConfigName(this.selectedPricingConfig);

                    if (configName && configName !== this.selectedPricingConfig) {
                      supplierData.currentCombination.newPricingConfig.configName = configName;
                      localStorage.setItem('pendingSupplierData', JSON.stringify(supplierData));
                    }
                  } catch (error) {
                    console.error('Error fetching pricing config name:', error);
                  }
                });
              });
            }
          }

          // Restore supplier creation tracking for create mode
          if (supplierData.createdSupplierUid) {
            this.supplierCreatedDuringPricing = true;
            this.createdSupplierUid = supplierData.createdSupplierUid;
          }

          // Handle edit mode - update the existing supplier with new pricing config
          if (supplierData.existingSupplierUid && supplierData.currentCombination?.newPricingConfig?.configId) {
            try {
              await this.updateSupplierAction({
                data: {
                  combinations: JSON.stringify(this.temporaryCombinations),
                },
                uid: supplierData.existingSupplierUid,
                notify: this.$root.notify,
              });

              this.$root.notify('success', 'Supplier updated with new pricing configuration', 3000);
            } catch (error) {
              console.error('Error updating supplier with new pricing config:', error);
              this.$root.notify('error', 'Failed to update supplier with new pricing configuration', 3000);
            }
          }
        } catch (e) {
          console.error('Error parsing supplier data from localStorage:', e);
        }
      }
    },
  },
};
</script>
